/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { CheckCircle2, ArrowDown, Clock, Loader2, Sparkles } from 'lucide-react';

const BUCKETS = [
  {
    key: 'finish_these_first',
    title: 'Finish these first',
    icon: CheckCircle2,
    tone: 'border-amber-500/30 bg-amber-500/5',
    iconTone: 'text-amber-300',
    empty: 'Nothing to finish from your previous phase.',
  },
  {
    key: 'continue_these',
    title: 'Continue these',
    icon: Sparkles,
    tone: 'border-emerald-500/30 bg-emerald-500/5',
    iconTone: 'text-emerald-300',
    empty: 'No items continuing from your previous phase.',
  },
  {
    key: 'start_these_after',
    title: 'Start these after',
    icon: ArrowDown,
    tone: 'border-cyan-500/30 bg-cyan-500/5',
    iconTone: 'text-cyan-300',
    empty: 'Nothing scheduled to start later.',
  },
  {
    key: 'do_not_start_yet',
    title: 'Do not start yet',
    icon: Clock,
    tone: 'border-slate-500/30 bg-slate-500/5',
    iconTone: 'text-slate-300',
    empty: 'Nothing on hold.',
  },
];

export default function TransitionPlan() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const r = await axios.get(`${API}/subscriber/formulation/transition-plan`);
      setData(r.data);
    } catch (e) {
      setPageError(e?.response?.data?.detail || 'Could not load your plan');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!user) return null;

  return (
    <div
      data-testid="subscriber-transition-plan-page"
      className="min-h-screen bg-slate-950 text-white px-6 py-10"
    >
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold mb-1">Your next phase</h1>
          <p className="text-sm text-white/50">
            Here&rsquo;s exactly what to do, in order.
          </p>
        </div>

        {loading && (
          <div className="rounded border border-white/10 bg-white/[0.02] p-8 text-center text-white/60">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Loading…
          </div>
        )}

        {pageError && !loading && (
          <div className="rounded border border-amber-500/30 bg-amber-500/5 p-6 text-center"
               data-testid="subscriber-transition-plan-error">
            <div className="text-sm text-white/80">{pageError}</div>
          </div>
        )}

        {!loading && !pageError && data && data.plan_active === false && (
          <div
            data-testid="subscriber-transition-plan-empty"
            className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center"
          >
            <Sparkles className="w-6 h-6 mx-auto text-white/30 mb-2" />
            <div className="text-sm text-white/70">
              Your next phase plan will appear here once your care team has finalized it.
            </div>
          </div>
        )}

        {!loading && data && data.plan_active && (
          <div className="space-y-5">
            {data.counselor_explanation && (
              <div
                data-testid="subscriber-transition-plan-explanation"
                className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4 text-sm text-cyan-100"
              >
                {data.counselor_explanation}
              </div>
            )}

            {BUCKETS.map((bucket) => (
              <BucketCard
                key={bucket.key}
                bucket={bucket}
                items={data.buckets?.[bucket.key] || []}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BucketCard({ bucket, items }) {
  const Icon = bucket.icon;
  return (
    <div
      data-testid={`subscriber-transition-bucket-${bucket.key}`}
      className={`rounded-xl border ${bucket.tone} p-4`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${bucket.iconTone}`} />
        <h2 className="text-sm font-semibold text-white">{bucket.title}</h2>
        <span className="ml-auto text-[10px] text-white/40">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-white/40 pl-6">{bucket.empty}</div>
      ) : (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <li
              key={i}
              data-testid={`subscriber-transition-item-${bucket.key}-${i}`}
              className="rounded-lg border border-white/5 bg-slate-950/40 p-3"
            >
              <div className="text-sm text-white">{it.item_name}</div>
              {it.instruction && (
                <div className="text-xs text-white/60 mt-1">{it.instruction}</div>
              )}
              {it.remaining_hint && (
                <div className="text-[10px] text-white/40 mt-1">{it.remaining_hint}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
